import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DashboardLayout } from "../../components/dashboard-layout";

const apiUrl = `${process.env.REACT_APP_API_URL}/auth/getall`;
const pageSize = 20;
const usersTheme = createTheme({
    typography: {
        fontFamily: "'Plus Jakarta Sans', 'Segoe UI', sans-serif",
        button: { textTransform: "none", fontWeight: 600 },
    },
});

const authConfig = () => {
    const token = localStorage.getItem("user");
    if (!token) throw new Error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.");
    return { headers: { Authorization: `Bearer ${token}` } };
};

const getErrorMessage = (error) =>
    error.response?.data?.message ||
    error.response?.data?.error?.message ||
    error.message ||
    "Không thể tải dữ liệu người chơi.";

function Users() {
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await axios.get(apiUrl, {
                ...authConfig(),
                params: { page, limit: pageSize, search: search.trim() },
            });
            setUsers(response.data?.data || []);
            setTotalPages(Math.max(1, Number(response.data?.totalPages) || 1));
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setLoading(false);
        }
    }, [page, search]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const toggleLock = async (user) => {
        const nextLocked = !user.isLock;
        const confirmed = await swal({
            title: nextLocked ? "Khóa tài khoản?" : "Mở khóa tài khoản?",
            text: `Người chơi "${user.username}" sẽ ${nextLocked ? "không thể" : "có thể"} đăng nhập.`,
            icon: "warning",
            buttons: ["Hủy", nextLocked ? "Khóa" : "Mở khóa"],
            dangerMode: nextLocked,
        });
        if (!confirmed) return;

        setError("");
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/auth/lockkey`,
                { id: user._id, isLock: nextLocked },
                authConfig()
            );
            setUsers((current) =>
                current.map((item) => item._id === user._id ? { ...item, isLock: nextLocked } : item)
            );
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        }
    };

    return (
        <ThemeProvider theme={usersTheme}>
            <DashboardLayout>
                <Box component="main" sx={{ py: 4, backgroundColor: "#F5F8FA", minHeight: "100vh" }}>
                    <Container maxWidth="lg">
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h5" fontWeight={800}>Quản lý người chơi</Typography>
                                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                                    Tìm và quản lý trạng thái tài khoản theo username người chơi.
                                </Typography>
                            </Box>

                            {error && <Alert severity="error">{error}</Alert>}

                            <Paper sx={{ p: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Tìm theo username hoặc mã người chơi"
                                    value={search}
                                    onChange={(event) => {
                                        setSearch(event.target.value);
                                        setPage(1);
                                    }}
                                />
                            </Paper>

                            <Paper sx={{ overflowX: "auto" }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Mã người chơi</TableCell>
                                            <TableCell>Username</TableCell>
                                            <TableCell>IP đăng nhập</TableCell>
                                            <TableCell>Ngày tạo</TableCell>
                                            <TableCell>Trạng thái</TableCell>
                                            <TableCell align="right">Thao tác</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    <CircularProgress size={24} />
                                                </TableCell>
                                            </TableRow>
                                        ) : users.length ? users.map((user) => (
                                            <TableRow key={user._id}>
                                                <TableCell>{user.iduser ?? "—"}</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>
                                                    {user.username || "—"}
                                                    {user.isDg2Player && (
                                                        <Chip label="DG2" size="small" color="primary" sx={{ ml: 1 }} />
                                                    )}
                                                </TableCell>
                                                <TableCell>{user.ip || "—"}</TableCell>
                                                <TableCell>
                                                    {user.createdAt
                                                        ? new Date(user.createdAt).toLocaleString("vi-VN")
                                                        : "—"}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        color={user.isLock ? "error" : "success"}
                                                        label={user.isLock ? "Đã khóa" : "Hoạt động"}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        color={user.isLock ? "success" : "error"}
                                                        onClick={() => toggleLock(user)}
                                                    >
                                                        {user.isLock ? "Mở khóa" : "Khóa"}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    Không tìm thấy người chơi.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>

                            <Stack direction="row" justifyContent="center">
                                <Pagination
                                    color="primary"
                                    page={page}
                                    count={totalPages}
                                    onChange={(_event, newPage) => setPage(newPage)}
                                />
                            </Stack>
                        </Stack>
                    </Container>
                </Box>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default Users;
